// src/app/services/brand/competitive-intelligence-api.ts
//
// The Competitive Intelligence v2 data layer, all of it from Phase 11:
//   * the two-plane ranking feed on /monitoring/measured-competitive
//   * per-competitor why_ahead reasons (Phase 11c)
//   * the attribute endpoints on the deep-signal router
//
// The client and the judged competitors are measured by the same instrument:
// the competitive scorer on the neutral-question subset, from the same stamps.
// The client row is not the full-battery funnel figure (that is
// measured-funnel-api). Anything unmeasured is null and stays null. Nothing
// here fills a gap with a number.
import { apiFetch } from "@/api/api-fetch";
import type { FunnelSource } from "./measured-funnel-api";

export type RankingInstrument =
  | "judged_neutral_subset"
  | "presence_only";

export interface RankingDeltas {
  primary_rec: number | null;
  top5: number | null;
  mention: number | null;
}

export interface WhyAheadReason {
  reason: string;
  win_share: number;
  quote: string;
}

export interface RankingRow {
  brand: string;
  instrument: RankingInstrument;
  primary_rec: number | null;
  top5: number | null;
  mention: number | null;
  deltas: RankingDeltas;
  /** Phase 11c. Empty when the brand has no judged wins. */
  why_ahead?: WhyAheadReason[];
  win_count?: number;
  why_ahead_low_confidence?: boolean;
}

export interface RankingFeed {
  run_id: number;
  previous_run_id: number | null;
  client: RankingRow;
  competitors: RankingRow[];
}

export interface MeasuredCompetitiveV2 {
  source: FunnelSource;
  brand: string;
  customerId: number | null;
  run: { id: number; sequence: number | null; completedAt: string | null } | null;
  focalName: string;
  ranking?: RankingFeed;
  instruments: { mention: string | null; top5: string | null; primary: string | null };
}

/**
 * How far apart two attribute scores must be before the difference is real.
 *
 * PROVISIONAL, pending repeat-judge validation. Derived from observation, not
 * from a designed experiment: re-judging run 4's Value on the identical
 * instrument moved Sunbit 65 -> 67 and Cherry 65 -> 64, so a three point gap
 * is inside what the judge produces from the same evidence twice. Brands
 * within this band share a rank; their internal order is not displayed
 * because it is not a measurement.
 */
export const NOISE_BAND_POINTS = 3.0;

export interface AttributeBrandRow {
  brand: string;
  score: number | null;
  /** The unrounded 0-100 score. Ranking uses this, never the integer. */
  score_precise?: number | null;
  mean_association?: number | null;
  unmeasured: boolean;
  span_count: number;
  /**
   * Spans the judge said ACTUALLY ADDRESS this attribute (phase 13). The mean
   * is taken over these alone; a span the judge shrugged at is evidence of
   * absence, not a middling opinion.
   */
  relevant_span_count?: number;
  /** Why there is no score: "signal" = spans exist but none address it. */
  unmeasured_reason?: "signal" | "no_spans" | null;
  candidate_span_count?: number;
  /**
   * Distinct models whose spans were SCORED for this brand and attribute
   * (phase 12e). Counted server-side from the judged spans. Deliberately not
   * derivable here: `quotes` is a capped sample and would understate it.
   */
  model_count?: number;
  quotes: { text: string; model: string }[];
  /** Clarity only. */
  agreement?: number | null;
  specificity?: number | null;
  labels_by_model?: Record<string, string[]>;
}

export interface AttributeResult {
  attribute: string;
  definition: string;
  scorer_id: string;
  computed_at: string;
  brands: AttributeBrandRow[];
  /** Match-first from the data layer; null when nothing matched. */
  action_id: string | null;
  generated_action_id: string | null;
}

export interface AttributesResponse {
  run_id: number;
  brands: string[];
  enabled: boolean;
  attributes: AttributeResult[];
}

export async function fetchMeasuredCompetitiveV2(brand: string): Promise<MeasuredCompetitiveV2> {
  const p = new URLSearchParams({ brand });
  return apiFetch<MeasuredCompetitiveV2>(`/monitoring/measured-competitive?${p}`);
}

export async function fetchAttributes(customerId: number, runId: number): Promise<AttributesResponse> {
  return apiFetch<AttributesResponse>(
    `/deep-signal/customers/${customerId}/runs/${runId}/attributes`,
  );
}

export async function addCustomAttribute(
  customerId: number, runId: number, name: string,
): Promise<AttributeResult> {
  return apiFetch<AttributeResult>(
    `/deep-signal/customers/${customerId}/runs/${runId}/attributes`,
    { method: "POST", body: JSON.stringify({ name }) },
  );
}

// ── Presentation rules, kept next to the contract they describe ────────────

/** Client plane: one decimal. Competitor plane: whole numbers. Null stays null. */
export function formatRate(v: number | null | undefined, plane: "client" | "competitor"): string | null {
  if (v == null) return null;
  const pct = v * 100;
  return plane === "client" ? pct.toFixed(1) : String(Math.round(pct));
}

/**
 * Movement renders ONLY when two consecutive reads confirm the direction.
 * The Phase 11 feed returns null deltas until a same-instrument previous read
 * exists, so this is quietly empty rather than a zero or a dash. A rendered
 * "0" would claim the metric was measured twice and did not move.
 */
export function formatMovement(delta: number | null | undefined): string {
  if (delta == null) return "";
  const pts = delta * 100;
  if (Math.abs(pts) < 0.05) return "";
  return `${pts > 0 ? "+" : ""}${pts.toFixed(pts >= 10 || pts <= -10 ? 0 : 1)}`;
}

export const STANDARD_ATTRIBUTE_ORDER = [
  "value", "trust", "innovation", "ease", "favorability", "clarity",
];

export function attributeLabel(slug: string): string {
  return slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, " ");
}

/**
 * Surface a held-back competitive-suggested row (phase 12e).
 *
 * Rows written by opening an evidence drawer stay out of the main Action
 * Items list until the user follows the drawer's link. Failure is swallowed
 * by the caller: navigation must not be blocked by the promotion.
 */
export async function promoteAction(actionId: string): Promise<void> {
  await apiFetch<{ promoted: boolean; id: string }>(
    `/fix-actions/action-log/${encodeURIComponent(actionId)}/promote`,
    { method: "POST" },
  );
}


// ── Attribute ranking (phase 13b) ──────────────────────────────────────────

export interface RankedAttributeRow {
  row: AttributeBrandRow;
  /** Shared across every brand inside the same noise band. */
  rank: number | null;
  /** True when this rank number is shared, so the UI can say so. */
  sharedRank: boolean;
}

/**
 * Rank attribute rows honestly.
 *
 * Three rules, each closing a real defect:
 *   1. rank on the UNROUNDED score. Ranking on the displayed integer let two
 *      different means tie, and the tie then fell to whoever sorted first.
 *   2. a genuine tie resolves to the NON-CLIENT brand. The client arrives
 *      first in the payload, so "stable sort" silently meant "client wins".
 *   3. brands within NOISE_BAND_POINTS share a rank number and are listed
 *      alphabetically, because their order is not a measurement.
 *
 * Unmeasured rows keep a null rank and sort last; they are never zero.
 */
export function rankAttributeRows(
  rows: AttributeBrandRow[], focalBrand: string,
): RankedAttributeRow[] {
  const value = (r: AttributeBrandRow) =>
    r.score_precise != null ? r.score_precise : (r.score != null ? r.score : null);

  const measured = rows.filter((r) => value(r) != null);
  const unmeasured = rows.filter((r) => value(r) == null);

  measured.sort((a, b) => {
    const d = (value(b) as number) - (value(a) as number);
    if (d !== 0) return d;
    return a.brand.localeCompare(b.brand);
  });

  const out: RankedAttributeRow[] = [];
  let i = 0;
  while (i < measured.length) {
    const leader = value(measured[i]) as number;
    let j = i;
    while (j < measured.length && leader - (value(measured[j]) as number) <= NOISE_BAND_POINTS) j++;
    const band = measured.slice(i, j);
    const rank = i + 1;
    const shared = band.length > 1;
    // Inside a band the order is not a result, so it is not presented as one.
    //
    // The client is listed LAST here, never first. Any exact tie necessarily
    // falls inside the band, so this is the only code path where a tie is
    // actually resolved: putting the rule in the outer comparator left it
    // unreachable, and the band re-sort silently decided it instead.
    if (shared) {
      band.sort((a, b) => {
        const aC = a.brand === focalBrand ? 1 : 0;
        const bC = b.brand === focalBrand ? 1 : 0;
        return aC - bC || a.brand.localeCompare(b.brand);
      });
    }
    for (const row of band) out.push({ row, rank, sharedRank: shared });
    i = j;
  }
  for (const row of unmeasured) out.push({ row, rank: null, sharedRank: false });
  return out;
}

/**
 * Attribute score for display. The client reports to one decimal and
 * competitors to whole numbers, the same two-plane rule the ranking table
 * uses; this panel printed bare integers for everyone.
 */
export function formatAttributeScore(
  row: AttributeBrandRow, focalBrand: string,
): string | null {
  const v = row.score_precise != null ? row.score_precise : row.score;
  if (v == null) return null;
  return row.brand === focalBrand ? v.toFixed(1) : String(Math.round(v));
}
